import { useEffect, useState } from "react";
import { Home, Wifi, BatteryFull } from "lucide-react";
import { useT } from "@/i18n";
import { ICONS, COLORS, APP_TEXT } from "@/components/appbuilder/missions";
import AppLogo from "@/components/appbuilder/AppLogo";
import FunBurst from "@/components/appbuilder/FunBurst";

export default function PhonePreview({ project, onNavigate }) {
  const { lang } = useT();
  const tx = APP_TEXT[lang];
  const screens = project.screens || [];
  const [current, setCurrent] = useState(0);
  const [trigger, setTrigger] = useState(0);
  useEffect(() => { if (current >= screens.length) setCurrent(0); }, [screens.length, current]);

  const screen = screens[current];
  const Icon = ICONS[screen?.icon] || ICONS.Star;
  const color = COLORS[screen?.color] || COLORS.sky;

  const go = (target) => {
    const idx = screens.findIndex((s) => s.id === target);
    if (idx < 0) return;
    setTrigger((t) => t + 1);
    setCurrent(idx);
    onNavigate?.();
  };

  return (
    <div className="mx-auto w-64 sm:w-72 h-[32rem] bg-slate-900 rounded-[2.8rem] p-3 shadow-2xl" data-testid="phone-preview">
      <div className="relative h-full bg-white rounded-[2.2rem] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center px-5 pt-2 text-[10px] font-bold text-slate-700">
          <span>9:41</span><span className="w-16 h-4 bg-slate-900 rounded-full" /><span className="flex gap-1"><Wifi className="w-3 h-3" /><BatteryFull className="w-3 h-3" /></span>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 border-b">
          <AppLogo logo={project.logo} size={30} />
          <span className="font-fredoka font-bold text-blue-950 truncate" data-testid="phone-app-name">{project.name || "Mi App"}</span>
        </div>
        {screen ? (
          <div className={`flex-1 bg-gradient-to-b ${color.grad} p-4 flex flex-col items-center text-center gap-2 overflow-y-auto`} data-testid="phone-screen">
            <div className="bg-white/80 rounded-3xl p-4 mt-2 shadow"><Icon className="w-12 h-12 text-blue-950" /></div>
            <p className="font-fredoka text-xl font-bold text-blue-950">{screen.title || `${tx.screen} ${current + 1}`}</p>
            <p className="text-sm font-semibold text-blue-950/80 break-words w-full">{screen.text}</p>
            <div className="mt-auto w-full space-y-2">
              {screen.buttons.filter((b) => b.label).map((b, i) => (
                <button key={i} type="button" onClick={() => go(b.target)} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full py-2.5 font-bold shadow" data-testid={`phone-btn-${i}`}>
                  {b.label}
                </button>
              ))}
              {current > 0 && (
                <button type="button" onClick={() => go(screens[0].id)} className="w-full bg-white/90 rounded-full py-2 font-bold text-blue-900 inline-flex items-center justify-center gap-1" data-testid="phone-home-btn">
                  <Home className="w-4 h-4" /> {tx.home}
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 bg-gradient-to-b from-sky-100 to-sky-300 p-4 text-center">
            <AppLogo logo={project.logo} size={88} />
            <p className="font-fredoka text-2xl font-bold text-blue-950">{project.name || "Mi App"}</p>
            <p className="text-sm font-semibold text-blue-900/70">{project.idea || tx.empty}</p>
          </div>
        )}
        <FunBurst type={project.fun} trigger={trigger} />
        <div className="h-1.5 w-24 bg-slate-900 rounded-full mx-auto my-2" />
      </div>
    </div>
  );
}
